import { useLocation, useNavigate } from "react-router-dom";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import {
  TextField,
  Button,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  FormControlLabel
} from "@mui/material";
import { useEffect, useState } from "react";
import { RESOURCES } from "../../constants/enum";
import { useAddPermissionMutation, useGetPermissionDetailQuery, useUpdatePermissionMutation } from "../../service/features/api";
import { toast } from "react-toastify";

export const RESOURCES_LABELS: Record<RESOURCES, string> = {
  [RESOURCES.DASHBOARD]: "Dashboard",
  [RESOURCES.CUSTOMER_MANAGEMENT]: "Customer Management",
  [RESOURCES.CATEGORIES_MANAGEMENT]: "Categories Management",
  [RESOURCES.LISTINGS_MANAGEMENT]: "Listings Management",
  [RESOURCES.VEHICLE_SPECIFICATION]: "Vehicle Specification",
  [RESOURCES.MANAGE_PACKAGES]: "Manage Packages",
  [RESOURCES.REVENUE_MANAGEMENT]: "Revenue Management",
  [RESOURCES.REPORT_ANALYTICS]: "Report & Analytics",
  [RESOURCES.NOTIFICATION_MANAGEMENT]: "Notification Management",
  [RESOURCES.SUB_ADMIN_MANAGEMENT]: "Sub-Admin Management",
  [RESOURCES.BLOG_MANAGEMENT]: "Blog Management",
  [RESOURCES.CMS_MANAGEMENT]: "CMS Management",
};


const AddRole = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const id = location.state?.id;

  const [roleName, setRoleName] = useState("");
  const [addPermission, { isLoading: isSaving }] = useAddPermissionMutation();
  const [updatePermission, { isLoading: isUpdating }] = useUpdatePermissionMutation();

  const { data: roleDetail, isLoading: isFetching } = useGetPermissionDetailQuery(id, { skip: !id });

  const modules = Object.entries(RESOURCES_LABELS)
    .filter(([value]) => Number(value) !== RESOURCES.SUB_ADMIN_MANAGEMENT)
    .map(([value, label]) => ({
      id: Number(value),
      name: label
    }));

  const [permissions, setPermissions] = useState(
    modules.map((module) => ({
      id: module.id,
      name: module.name,
      view: module.id === RESOURCES.DASHBOARD && !id,
      add: false,
      edit: false,
      delete: false
    }))
  );

  useEffect(() => {
    if (roleDetail?.data) {
      const { name, permission } = roleDetail.data;
      setRoleName(name);
      setPermissions(prev => prev.map(m => {
        const p = permission.find((item: any) => item.sideBarId === m.id);
        return p ? {
          ...m,
          view: p.isView,
          add: p.isAdd,
          edit: p.isEdit,
          delete: p.isDelete
        } : m;
      }));
    }
  }, [roleDetail]);

  const handlePermissionChange = (id: number, field: string) => {
    setPermissions(prev =>
      prev.map(row => {
        if (row.id === id) {
          const newValue = !row[field as keyof typeof row];
          const newRow = { ...row, [field]: newValue };

          // If enabling add, edit, or delete, must enable view
          if (newValue && (field === "add" || field === "edit" || field === "delete")) {
            newRow.view = true;
          }

          // If disabling view, must disable all others
          if (!newValue && field === "view") {
            newRow.add = false;
            newRow.edit = false;
            newRow.delete = false;
          }

          return newRow;
        }
        return row;
      })
    );
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      toast.error("Please enter a role name");
      return;
    }

    if (roleName.length > 40) {
      toast.error("Role name should not be more than 40 characters");
      return;
    }

    const permissionPayload = permissions
      .filter(p => p.view || p.add || p.edit || p.delete)
      .map(p => ({
        sideBarId: p.id,
        label: p.name,
        isView: p.view,
        isAdd: p.add,
        isEdit: p.edit,
        isDelete: p.delete
      }));

    if (permissionPayload.length === 0) {
      toast.error("Please select at least one module");
      return;
    }

    try {
      if (id) {
        const response = await updatePermission({
          id,
          name: roleName,
          permission: permissionPayload
        }).unwrap();
        toast.success(response.message || "Role updated successfully", {
          autoClose: 2000
        });
      } else {
        const response = await addPermission({
          name: roleName,
          permission: permissionPayload
        }).unwrap();
        toast.success(response.message || "Role created successfully", {
          autoClose: 2000
        });
      }
      setTimeout(() => {
        navigate("/manage-subadmin/roles");
      }, 2000);
    } catch (error: any) {
      toast.error(error?.data?.message || `Failed to ${id ? "update" : "create"} role`);
    }
  };

  if (isFetching) return <div className="loading_state">Loading...</div>;

  return (
    <>
      <CommonBreadcrumb
        title={id ? "Edit Role" : "Add Role"}
        breadcrumbItems={[
          { label: "Dashboard", onClick: () => navigate("/dashboard") },
          { label: "Manage Sub-Admin", onClick: () => navigate("/manage-subadmin") },
          { label: "Manage Roles", onClick: () => navigate("/manage-subadmin/roles") },
          { label: "Role Detail" },
        ]}
      />

      <div className="cards">
        <div className="form">
          <div className="gap_p">
            <div className="control_group w_50">
              <label htmlFor="role">Role Name</label>
              <TextField 
                id="role" 
                placeholder="Enter Role Name" 
                hiddenLabel 
                fullWidth 
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                inputProps={{ maxLength: 40 }}
              />
            </div>
          </div>

          <TableContainer className="table_container mt_20">
            <Table>
              <TableHead>
                <TableRow hover>
                  <TableCell>Module Name</TableCell>
                  <TableCell align="center">View</TableCell>
                  <TableCell align="center">Add</TableCell>
                  <TableCell align="center">Edit</TableCell>
                  <TableCell align="center">Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissions.map(row => (
                  <TableRow hover key={row.id}>
                    <TableCell>
                      {row.name.includes(" - ") ? (
                        <span style={{ paddingLeft: 20 }}>{row.name.split(" - ")[1]}</span>
                      ) : (
                        <strong>{row.name}</strong>
                      )}
                    </TableCell>
                    {["view", "add", "edit", "delete"].map(action => (
                      <TableCell align="center" key={action}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(row[action as keyof typeof row])}
                              onChange={() => handlePermissionChange(row.id, action)}
                            />
                          }
                          name="check"
                          label=""
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <div className="form_btn">
            <Button 
              className="btn btn_primary" 
              onClick={handleSave}
              disabled={isSaving || isUpdating}
            >
              {isSaving || isUpdating ? "Saving..." : id ? "Update" : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddRole;
